import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { handleGenericError } from '../handle-generic-error';
import authSlice from '../auth/auth-slice';

export const fetchStageFormData = createAsyncThunk(
  'issues/fetch-stage-form-data',
  async ({ itemAssignmentCode, navigate }, thunkAPI) => {
    const zensignApiUrl = window.config.zensignApiUrl;
    await axios
      .get(`${zensignApiUrl}/external-assignments/document/item-assignments/${itemAssignmentCode}`)
      .then((response) => {
        thunkAPI.dispatch(issuesSlice.actions.fetchStageFormDataSuccess(response.data));
      })
      .catch((err) => {
        if (err.response.status === 403) {
          thunkAPI.dispatch(authSlice.actions.sessionConflict(itemAssignmentCode));
          throw thunkAPI.rejectWithValue({ ...err.response.data });
        }
        handleGenericError(thunkAPI.dispatch, err, navigate);
        throw thunkAPI.rejectWithValue({ ...err.response.data });
      });
  }
);

export const issueApprove = createAsyncThunk(
  'issues/issue-approve',
  async ({ itemAssignmentCode, signatoryEmail, role, securityCode, comment, isApproved, navigate }, thunkAPI) => {
    const zensignApiUrl = window.config.zensignApiUrl;
    return await axios
      .post(`${zensignApiUrl}/external-assignee/verify/sign`, {
        email: signatoryEmail,
        role: role,
        code: itemAssignmentCode,
        securityCode: securityCode,
        comment: comment,
        isApproved: isApproved,
      })
      .then((response) => {
        isApproved
          ? thunkAPI.dispatch(issuesSlice.actions.documentApproved(response.data))
          : thunkAPI.dispatch(issuesSlice.actions.documentRejected(response.data));

        return response.data;
      })
      .catch((err) => {
        if (err.response.status === 422 && err.response.data.errors) {
          const errorMessageKey = err.response.data.errors[0].message;

          throw thunkAPI.rejectWithValue({
            issueApprovalStatus: 'Invalid',
            errorCode: errorMessageKey,
          });
        } else {
          handleGenericError(thunkAPI.dispatch, err, navigate);
          throw thunkAPI.rejectWithValue({ ...err.response.data });
        }
      });
  }
);

export const acceptTos = createAsyncThunk('issues/update-tos', async ({ itemAssignmentCode, navigate }, thunkAPI) => {
  const zensignApiUrl = window.config.zensignApiUrl;
  await axios
    .put(`${zensignApiUrl}/external-assignments/document/item-assignments/${itemAssignmentCode}/accept-tos`)
    .then((response) => {
      thunkAPI.dispatch(issuesSlice.actions.tosAccepted(response.data));
    })
    .catch((err) => {
      handleGenericError(thunkAPI.dispatch, err, navigate);
      throw thunkAPI.rejectWithValue({ ...err.response.data });
    });
});

function getTosAcceptedState(action) {
  return action.payload.data.tosAccepted ? 'Accepted' : 'NotAccepted';
}

const issuesSlice = createSlice({
  name: 'issues',
  initialState: {
    issue: {
      stage: {
        formData: { data: {}, errors: {} },
      },
      signed: false,
      approvalStatus: '',
      approvalError: '',
      tosStatus: '',
      signedType: '',
    },
  },
  reducers: {
    fetchStageFormDataSuccess: (state, action) => {
      return {
        ...state,
        issue: {
          ...state.issue,
          stage: {
            formData: action.payload,
          },
          tosStatus: getTosAcceptedState(action),
        },
      };
    },
    documentApproved: (state) => {
      return {
        ...state,
        issue: {
          ...state.issue,
          signed: true,
          signedType: 'approved',
          approvalError: '',
        },
      };
    },
    documentRejected: (state) => {
      return {
        ...state,
        issue: {
          ...state.issue,
          signed: true,
          signedType: 'rejected',
          approvalError: '',
        },
      };
    },
    tosAccepted: (state) => {
      return {
        ...state,
        issue: {
          ...state.issue,
          tosStatus: 'Accepted',
        },
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(issueApprove.fulfilled, (state) => {
        state.issue.approvalStatus = 'Success';
      })
      .addCase(issueApprove.pending, (state) => {
        state.issue.approvalStatus = 'Pending';
      })
      .addCase(issueApprove.rejected, (state, action) => {
        state.issue.approvalStatus = action.payload.issueApprovalStatus;
        state.issue.approvalError = action.payload.errorCode;
      })
      .addDefaultCase(() => {});
  },
});

export default issuesSlice;
export const { reducer } = issuesSlice;
export const { fetchStageFormDataSuccess, documentApproved } = issuesSlice.actions;
